import React, { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { getEmployeesOwnedDocuments } from '../../../api';

import styles from './styles.module.scss';

const EmployeesOwnedDocuments = () => {
  const { id: companyId } = useParams();
  const { t } = useTranslation();
  const history = useHistory();

  const [documents, setDocuments] = useState([]);

  useEffect(() => {
    const fetchDocuments = async () => {
      const res = await getEmployeesOwnedDocuments(companyId);
      if (Array.isArray(res?.data)) {
        setDocuments(res.data);
      } else if (Array.isArray(res)) {
        setDocuments(res);
      }
    };

    if (companyId) fetchDocuments();
  }, [companyId]);

  const getDueDateLabel = (dueDate) => {
    if (!dueDate) return null;

    const due = moment(dueDate);
    if (!due.isValid()) return null;

    const diffDays = due.clone().startOf('day').diff(moment().startOf('day'), 'days');
    let relative = t('today');
    if (diffDays > 0) {
      relative = t('in {{count}} days', { count: diffDays });
    } else if (diffDays < 0) {
      relative = t('{{count}} days ago', { count: Math.abs(diffDays) });
    }

    return `${t('due {{date}}', { date: due.format('MMM D, YYYY') })} · ${relative}`;
  };

  const handleClickDocument = (doc) => () => {
    history.push({
      search: `?tab=required_documents&document_id=${doc.document_id}`,
    });
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerInfo}>
          <div className={styles.headerTitle}>{t('Required documents')}</div>
          <div className={styles.headerDesc}>{t('Documents employees must provide')}</div>
        </div>
      </div>
      <div className={styles.content}>
        <div className={styles.grid}>
          {
            documents.map((doc) => {
              const requiredCount = Number(doc.required_count) || 0;
              const completedCount = Number(doc.complete_count) || 0;
              const inReviewCount = Number(doc.in_review_count) || 0;
              const completionPercentage = Math.max(0, Math.min(100, Number(doc.completion_percentage) || 0));
              const inReviewPercentage = requiredCount
                ? Math.min(100 - completionPercentage, (inReviewCount / requiredCount) * 100)
                : 0;

              return (
                <button
                  key={doc.document_id}
                  type='button'
                  className={styles.card}
                  onClick={handleClickDocument(doc)}
                >
                  <div className={styles.cardHeader}>
                    <div className={styles.cardInfo}>
                      <div className={styles.category}>
                        {doc.category?.name}
                        {doc.place?.name ? ` · ${doc.place.name}` : ''}
                      </div>
                      <div className={styles.title}>{doc.document_name || doc.document_type_name}</div>
                      <div className={styles.dueDate}>{getDueDateLabel(doc.due_date_for_action)}</div>
                    </div>
                    <svg
                      className={styles.chevron}
                      width='7'
                      height='12'
                      viewBox='0 0 7 12'
                      fill='none'
                      aria-hidden='true'
                    >
                      <path
                        d='M1 1L6 6L1 11'
                        stroke='currentColor'
                        strokeWidth='1.4'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  </div>
                  <div
                    className={styles.progress}
                    role='img'
                    aria-label={t('{{percentage}}% complete', { percentage: completionPercentage })}
                  >
                    <div className={styles.progressComplete} style={{ width: `${completionPercentage}%` }} />
                    <div className={styles.progressInReview} style={{ width: `${inReviewPercentage}%` }} />
                  </div>
                  <div className={styles.stats}>
                    <div className={styles.stat}>
                      <div className={styles.statValueGreen}>{completedCount}</div>
                      <div className={styles.statLabel}>{t('Complete')}</div>
                    </div>
                    <div className={styles.stat}>
                      <div className={styles.statValueBlue}>{inReviewCount}</div>
                      <div className={styles.statLabel}>{t('In review')}</div>
                    </div>
                    <div className={styles.stat}>
                      <div className={styles.statValue}>{Number(doc.still_needed_count) || 0}</div>
                      <div className={styles.statLabel}>{t('Still needed')}</div>
                    </div>
                    <div className={styles.stat}>
                      <div className={doc.overdue_count ? styles.statValueRed : styles.statValue}>
                        {Number(doc.overdue_count) || 0}
                      </div>
                      <div className={styles.statLabel}>{t('Overdue')}</div>
                    </div>
                  </div>
                  <div className={styles.footer}>
                    <div className={styles.badge}>
                      <span className={styles.badgeDot} />
                      {t('{{percentage}}% complete', { percentage: completionPercentage })}
                    </div>
                    <div className={styles.footerText}>
                      {t('{{loaded}} employees loaded of {{total}}', {
                        loaded: Number(doc.complete_count) || 0,
                        total: requiredCount,
                      })}
                    </div>
                  </div>
                </button>
              );
            })
          }
        </div>
      </div>
    </div>
  );
};

export default EmployeesOwnedDocuments;
